import classNames from 'classnames';
import { useIntl } from 'react-intl';
import Close from '../../icons/Close';
import { LinkInfo, Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import LinkButtonOrDiv from '../LinkButtonOrDiv';
import styles from './styles/styles.module.scss';

export type TagVariant = 'default' | 'custom';

type TagProps = {
  className?: string;
  /** Accessible label for the dismiss control. Defaults to "Remove {text}". */
  dismissLabel?: string;
  /** Renders a trailing close (X) control that fires onDismiss when activated. */
  dismissable?: boolean;
  leadingIcon?: IconDefinition<'orientation'>;
  linkInfo?: LinkInfo;
  onClick?: () => void;
  onDismiss?: () => void;
  size?: Size<'sm' | 'md'>;
  text: string;
  /** `custom` uses the branded/blue fill. */
  variant?: TagVariant;
};

const Tag = ({
  className,
  dismissLabel,
  dismissable = false,
  leadingIcon,
  linkInfo,
  onClick,
  onDismiss,
  size = 'md',
  text,
  variant = 'default'
}: TagProps) => {
  const intl = useIntl();
  const iconColor = variant === 'custom' ? '--color-text-info' : '--color-text-secondary';
  const dismissAriaLabel = dismissLabel ?? intl.formatMessage({ defaultMessage: 'Remove {tag}' }, { tag: text });

  return (
    <div
      {...setDenaliAttribute('Tag')}
      className={classNames(className, styles.container, styles[size], styles[variant], { [styles.dismissable]: dismissable })}
    >
      <LinkButtonOrDiv className={styles.tag} linkInfo={linkInfo} onClick={onClick}>
        {leadingIcon ? <IconRenderer icon={leadingIcon} defaults={{ color: iconColor, size: 'sm' }} /> : null}
        <span className={styles.textContent}>{text}</span>
      </LinkButtonOrDiv>
      {dismissable ? (
        <button aria-label={dismissAriaLabel} className={styles.dismissButton} onClick={onDismiss} title={dismissAriaLabel} type="button">
          <Close color="currentColor" size="sm" />
        </button>
      ) : null}
    </div>
  );
};

export default Tag;
